import type { ConnectionPhase } from '@parsleyai/ui'
import { type DocumentData, type DocumentSnapshot, doc, onSnapshot, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore'
import { createContext, type ReactNode, useCallback, useEffect, useReducer } from 'react'
import { normalizeCuisinePreferences } from '@/lib/eatingStyle/cuisines'
import { readEffort } from '@/lib/eatingStyle/effort'
import { db } from '@/services/firebase/config'
import { useLiveSubscription } from '@/store/userData/useLiveSubscription'
import { useUserData } from '@/store/userData/useUserData'
import type {
  CuisinePreferences,
  EatingPreferences,
  EffortPreferences,
  FrequencyLimit,
  NutritionTargets,
  PlateShape
} from '@/types/eatingPreferences'
import { Budget } from '@/types/mealPlan'
import { eatingPreferencesReducer, initialEatingPreferencesState } from './eatingPreferencesReducer'
import { EatingPreferencesActionTypes, type EatingPreferencesState } from './types'

export interface EatingPreferencesContextValue {
  state: EatingPreferencesState
  updatePlateShape: (plateShape: PlateShape | null) => Promise<void>
  updateCuisinePreferences: (cuisinePreferences: CuisinePreferences | null) => Promise<void>
  /** Effort and budget share one screen and one Save, so they're written together. */
  updateCookingEffort: (effort: EffortPreferences | null, budget: Budget | null) => Promise<void>
  updateNutritionTargets: (nutritionTargets: NutritionTargets | null) => Promise<void>
  updateFrequencyLimits: (frequencyLimits: FrequencyLimit[] | null) => Promise<void>
  /** Live-connection phase for the subscription (auto-resubscribes on drop; banner-ready). */
  phase: ConnectionPhase
  /** Manual resubscribe. */
  retry: () => void
}

export const EatingPreferencesContext = createContext<EatingPreferencesContextValue | undefined>(undefined)

function getDocRef(uid: string) {
  return doc(db, 'users', uid, 'eating_preferences', 'default')
}

/** The one place stored preferences enter the app, and so the one place old shapes are read. */
function readPreferences(data: DocumentData): EatingPreferences {
  return {
    ...(data as EatingPreferences),
    cuisinePreferences: normalizeCuisinePreferences(data.cuisinePreferences),
    effort: readEffort(data.effort),
    budget: Object.values(Budget).includes(data.budget) ? data.budget : null
  }
}

async function upsert(uid: string, fields: Partial<EatingPreferences>) {
  const ref = getDocRef(uid)
  try {
    await updateDoc(ref, { ...fields, updatedAt: serverTimestamp() })
  } catch {
    // Document doesn't exist yet — create it
    await setDoc(ref, {
      plateShape: null,
      cuisinePreferences: null,
      effort: null,
      budget: null,
      nutritionTargets: null,
      frequencyLimits: null,
      ...fields,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
  }
}

export function EatingPreferencesProvider({ children }: { children: ReactNode }) {
  const { uid } = useUserData()
  const [state, dispatch] = useReducer(eatingPreferencesReducer, initialEatingPreferencesState)

  // Drop cached prefs on sign-out; the subscription itself is disabled by the null key below.
  useEffect(() => {
    if (!uid) dispatch({ type: EatingPreferencesActionTypes.CLEAR })
  }, [uid])

  const { phase, retry } = useLiveSubscription<DocumentSnapshot<DocumentData>>({
    subscriptionKey: uid,
    label: 'eatingPreferences',
    subscribe: (activeUid, { onData, onError }) => onSnapshot(getDocRef(activeUid), onData, onError),
    onData: (snapshot) => {
      dispatch({
        type: EatingPreferencesActionTypes.SET_PREFERENCES,
        payload: snapshot.exists() ? readPreferences(snapshot.data()) : null
      })
    }
  })

  const updatePlateShape = useCallback(
    async (plateShape: PlateShape | null) => {
      if (!uid) return
      await upsert(uid, { plateShape })
    },
    [uid]
  )

  const updateCuisinePreferences = useCallback(
    async (cuisinePreferences: CuisinePreferences | null) => {
      if (!uid) return
      await upsert(uid, { cuisinePreferences })
    },
    [uid]
  )

  const updateCookingEffort = useCallback(
    async (effort: EffortPreferences | null, budget: Budget | null) => {
      if (!uid) return
      await upsert(uid, { effort, budget })
    },
    [uid]
  )

  const updateNutritionTargets = useCallback(
    async (nutritionTargets: NutritionTargets | null) => {
      if (!uid) return
      await upsert(uid, { nutritionTargets })
    },
    [uid]
  )

  const updateFrequencyLimits = useCallback(
    async (frequencyLimits: FrequencyLimit[] | null) => {
      if (!uid) return
      await upsert(uid, { frequencyLimits })
    },
    [uid]
  )

  return (
    <EatingPreferencesContext.Provider
      value={{
        state,
        updatePlateShape,
        updateCuisinePreferences,
        updateCookingEffort,
        updateNutritionTargets,
        updateFrequencyLimits,
        phase,
        retry
      }}>
      {children}
    </EatingPreferencesContext.Provider>
  )
}
